import { makeTrustedClock, type TrustedClock } from '@office/domain'

/**
 * The trusted clock (docs/02 §4).
 *
 * On an offline tablet a human sets the system clock by hand, and if the
 * device's tzdata predates Egypt's 2023 DST reinstatement, Android converts that
 * typed time into an epoch that is a FULL HOUR wrong. Formatting fixes cannot
 * touch this: the prayer table would print correctly while the adhan fired an
 * hour early in real time.
 *
 * So we never trust the device epoch. We sync a correction from a phone or the
 * server and store the DELTA, then read every "what time is it" through here.
 * We deliberately sync the EPOCH, never a wall-clock string — a string would be
 * re-parsed through the same stale offset and reintroduce the bug.
 */
export interface ClockState {
  skewMs: number
  lastSyncedAt: number | null
}

export const UNSYNCED: ClockState = { skewMs: 0, lastSyncedAt: null }

/** Derive the correction from a server epoch and the device's own reading. */
export function computeSkew(serverEpochMs: number, deviceEpochMs: number): number {
  return serverEpochMs - deviceEpochMs
}

export function clockFrom(state: ClockState, deviceNow: () => number = Date.now): TrustedClock {
  return makeTrustedClock({
    deviceNow,
    skewMs: state.skewMs,
    lastSyncedAt: state.lastSyncedAt,
  })
}

export const HOUR = 3_600_000
export const DAY = 86_400_000

export type ClockTrust = 'synced' | 'stale' | 'never' | 'suspect'

/**
 * How much the dashboard should trust its own clock. This is surfaced as a
 * first-class element on the board, not buried in a settings screen: it is the
 * only thing that makes a one-hour epoch error visible to the people who notice
 * the adhan firing at the wrong time but cannot tell why.
 */
export function clockTrust(state: ClockState, nowMs: number): ClockTrust {
  if (state.lastSyncedAt === null) return 'never'
  // A correction near a whole hour is the signature of stale tzdata, not drift.
  if (Math.abs(state.skewMs) > 30 * 60_000) return 'suspect'
  const age = nowMs - state.lastSyncedAt
  return age > 7 * DAY ? 'stale' : 'synced'
}

export function describeTrust(t: ClockTrust, state: ClockState, nowMs: number): string {
  switch (t) {
    case 'never': return 'Clock never synced — prayer times may be wrong'
    case 'suspect': {
      const h = (state.skewMs / HOUR).toFixed(1)
      return `Device clock is ${h}h out — corrected, but check the tablet's time`
    }
    case 'stale': {
      const days = Math.floor((nowMs - (state.lastSyncedAt ?? nowMs)) / DAY)
      return `Clock last synced ${days} days ago`
    }
    case 'synced': return 'Clock synced'
  }
}
